import { loadImage } from 'src/atoms/utils/image-utils'
import { PageRenderParams, PagesRenderConfig, PageElement } from 'src/types/report-insights'

export const getRenderConfig = async (params: PageRenderParams): Promise<PagesRenderConfig> => {
  const {
    pageSize,
    pageDimensions,
    pageColor,
    orientation,
    pageMargin,
    images,
    pageHeader,
    pageFooter,
    coverPage,
    logoDimensions,
  } = params
  const gap = pageMargin / 4

  // Set page size based on orientation
  const pageWidth = orientation === 'portrait' ? pageDimensions.width : pageDimensions.height
  const pageHeight = orientation === 'portrait' ? pageDimensions.height : pageDimensions.width

  const pageHeaderHeight = pageHeader ? pageMargin / 2 + pageHeader.height : 0

  // Max available height on a per page
  const maxAvailableHeight = () => {
    let height = pageHeight - 2 * pageMargin
    if (pageHeader) {
      height -= pageHeader.height
      height -= gap
    }
    if (pageFooter) {
      height -= pageFooter.height
      height -= gap
    }
    return height
  }

  let availableHeight = maxAvailableHeight()
  let yPosition = pageMargin + pageHeaderHeight // y position starts after top margin and page header

  // Track pages
  const pages = []
  // Track page elements
  let pageElements: PageElement[] = []

  // Initial page setup
  const init = () => {
    // reset available height and y position
    availableHeight = maxAvailableHeight()
    yPosition = pageMargin + pageHeaderHeight

    // Add header element to page elements if it exists
    if (pageHeader) {
      const headerElement: PageElement = {
        xPosition: pageMargin,
        yPosition: pageMargin,
        height: pageHeader.height,
        width: pageWidth - 2 * pageMargin,
        type: 'header',
        title: {
          text: pageHeader.title.text,
          fontSize: pageHeader.title.fontSize,
          fontColor: pageHeader.title.fontColor,
        },
        subtitle: {
          text: pageHeader.subtitle.text,
          fontSize: pageHeader.subtitle.fontSize,
          fontColor: pageHeader.subtitle.fontColor,
        },
      }
      pageElements.push(headerElement)
    }

    // Add footer element to page elements if it exists
    if (pageFooter) {
      const footerElement: PageElement = {
        xPosition: 0,
        yPosition: pageHeight - pageFooter.height,
        height: pageFooter.height,
        width: pageWidth,
        type: 'footer',
        logo: pageFooter.logo,
        color: pageFooter.color,
        pageNumber: pages.length + 1,
      }
      pageElements.push(footerElement)
    }
  }
  init()

  // Loop through images
  for (let i = 0; i < images.length; i++) {
    const image = images[i]

    if (!image) {
      if (pageElements.length > 0) {
        pages.push(pageElements)
        pageElements = []
      }
      continue
    }

    const img = await loadImage(image)

    // Calculate which adjustment is the smallest, width or height
    const widthPercent = (pageWidth - 2 * pageMargin) / img.width
    // Whats the max height an element can be on a page
    const maxElementHeight = maxAvailableHeight()
    const heightPercent = maxElementHeight / img.height
    const smallestPercent = Math.min(widthPercent, heightPercent)

    // Max dimensions of image that will fit on a page
    const width = img.width * smallestPercent
    const height = img.height * smallestPercent

    // Center image if there is excess X-axis space else align to the left
    const xPosition = width < pageWidth ? (pageWidth - width) / 2 : pageMargin

    // Image will overflow the available space left on the page; add a new page
    if (height > availableHeight && pageElements.some((element) => element.type === 'image')) {
      // Push current page elements onto pages
      pages.push(pageElements)

      // New page
      pageElements = [] // Reset page elements
      init() // Initialize new page with header and footer if they exist
    }

    // Push onto page elements
    pageElements.push({
      xPosition,
      yPosition,
      type: 'image',
      image,
      width,
      height,
    })

    if (i === images.length - 1) {
      pages.push(pageElements)
    }

    // Update the available height and y position for the next image
    availableHeight -= height
    availableHeight -= gap
    yPosition += height + gap
  }

  return {
    coverPage,
    logoDimensions,
    page: {
      size: pageSize,
      width: pageWidth,
      height: pageHeight,
      orientation,
      color: pageColor,
    },
    pages,
  }
}
